import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import {
  addSelectedField,
  addSelectedFilter,
  checkSelectedWidgets,
  clearSelectedFields,
  clearSelectedFilters,
  clearSelectedWidgets,
  clearTree,
  fetchAllDashboardsFailure,
  fetchAllDashboardsRequest,
  fetchAllDashboardsSuccess,
  removeSelectedField,
  removeSelectedFilter,
  requestTreeFiltersToogle,
  SelectedFieldPayload,
  SelectedFilterPayload,
  setActiveDashboard,
  setTimeRange,
  setTree,
  signalExporting,
  TimeRange,
  upsertDashboardRequest,
  upsertDashboardSuccess,
} from 'actions/dashboard';
import { Dashboard } from 'types';
import { DataNode, Operator, Tree, ValueNode } from 'utils/parserTree';
import { v4 } from 'uuid';

type DashboardState = {
  dashboards: { [name: string]: Dashboard };
  activeUuid: string | null;
  loading: string[];
  timeRange: {
    after: string;
    before: string;
  };
  exporting: number;
  selectedWidgets: string[];
  selectedFilters: ValueNode[];
  selectedFields: DataNode[];
  filtersTree: Tree;
  requestTreeFilters: boolean;
};

type DashboardReducer<P = void> = (
  state: DashboardState,
  payload: PayloadAction<P>
) => DashboardState;

const handleFetchAllRequest: DashboardReducer = (state) => ({
  ...state,
  error: null,
  loading: [...state.loading, fetchAllDashboardsRequest.toString()],
});

const handleFetchAllSuccess: DashboardReducer<{ records: Dashboard[] }> = (state, { payload }) => ({
  ...state,
  dashboards: payload?.records?.reduce((acc, item) => {
    const resources: { [name: string]: Dashboard } = { ...acc };
    resources[item.uuid] = item;
    return resources;
  }, {}),
  loading: state.loading.filter((s) => s !== fetchAllDashboardsRequest.toString()),
});

const handleFetchAllFailure: DashboardReducer = (state, { payload }) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== fetchAllDashboardsRequest.toString()),
});

const handleSetActiveDashboard: DashboardReducer<string | null> = (state, { payload }) => ({
  ...state,
  activeUuid: payload,
});

const handleUpsertDashboardRequest: DashboardReducer = (state) => ({
  ...state,
  error: null,
  loading: [...state.loading, upsertDashboardRequest.toString()],
});

const handleUpsertDashboardSuccess: DashboardReducer = (state) => ({
  ...state,
  error: null,
  loading: state.loading.filter((s) => s !== upsertDashboardRequest.toString()),
});

const handleSetTimeRange: DashboardReducer<TimeRange> = (state, { payload }) => ({
  ...state,
  timeRange: {
    ...state.timeRange,
    after: payload.after,
    before: payload.before,
  },
});

const handleSingnalExporting: DashboardReducer = (state) => ({
  ...state,
  exporting: state.exporting + 1,
});

const handleCheckSelectedWidgets: DashboardReducer<string> = (state, { payload }) => ({
  ...state,
  selectedWidgets: state.selectedWidgets.includes(payload)
    ? state.selectedWidgets.filter((w) => w !== payload)
    : [...state.selectedWidgets, payload],
});

const handleClearSelectedWidgets: DashboardReducer<string> = (state) => ({
  ...state,
  selectedWidgets: [],
});

const handleAddSelectedFilter: DashboardReducer<SelectedFilterPayload> = (
  state: DashboardState,
  { payload }
) => {
  const selectedFilters = [...state.selectedFilters];
  if (
    selectedFilters.some(
      (filter) => filter.id !== payload.node.id && payload.node.parent !== filter.parent
    )
  ) {
    return {
      ...state,
      selectedFilters: [payload.node],
    };
  }
  return {
    ...state,
    selectedFilters: [...state.selectedFilters, payload.node],
  };
};

const handleRemoveSelectedFilter: DashboardReducer<SelectedFilterPayload> = (
  state: DashboardState,
  { payload }
) => {
  const selectedFilters = [...state.selectedFilters];
  const index = selectedFilters.findIndex((filter) => filter.id === payload.node.id);
  selectedFilters.splice(index, 1);
  return {
    ...state,
    selectedFilters,
  };
};

const handleSetTree: DashboardReducer<{ tree: Tree }> = (state: DashboardState, { payload }) => ({
  ...state,
  filtersTree: payload.tree,
});

const handleClearAllSelectedFilters: DashboardReducer = (state: DashboardState) => ({
  ...state,
  selectedFilters: [],
});

const handleAddSelectedField: DashboardReducer<SelectedFieldPayload> = (
  state: DashboardState,
  { payload }
) => {
  const selectedFields = [...state.selectedFields];

  if (
    selectedFields.some(
      (field) => field.id !== payload.node.id && payload.node.parent !== field.parent
    )
  ) {
    return {
      ...state,
      selectedFields: [payload.node],
    };
  }

  return {
    ...state,
    selectedFields: [...state.selectedFields, payload.node],
  };
};

const handleRemoveSelectedField: DashboardReducer<SelectedFieldPayload> = (
  state: DashboardState,
  { payload }
) => {
  const selectedFields = [...state.selectedFields];
  const index = selectedFields.findIndex((field) => field.id === payload.node.id);
  selectedFields.splice(index, 1);
  return {
    ...state,
    selectedFields,
  };
};

const handleClearAllSelectedFields: DashboardReducer = (state: DashboardState) => ({
  ...state,
  selectedFields: [],
});

const handleChangeRequestTree: DashboardReducer<{ value: boolean }> = (
  state: DashboardState,
  { payload }
) => ({
  ...state,
  requestTreeFilters: payload.value,
});

const handleClearTree: DashboardReducer = (state: DashboardState) => ({
  ...state,
  filtersTree: {
    op: Operator.AND,
    data: [],
    id: v4(),
  },
});

const defaultState: DashboardState = {
  dashboards: {},
  activeUuid: null,
  loading: [],
  timeRange: {
    after: '',
    before: '',
  },
  exporting: 0,
  selectedWidgets: [],
  selectedFilters: [],
  selectedFields: [],
  filtersTree: { op: 'and', data: [], id: v4() },
  requestTreeFilters: true,
};

const handlers = {
  [fetchAllDashboardsRequest.toString()]: handleFetchAllRequest,
  [fetchAllDashboardsSuccess.toString()]: handleFetchAllSuccess,
  [fetchAllDashboardsFailure.toString()]: handleFetchAllFailure,
  [setActiveDashboard.toString()]: handleSetActiveDashboard,
  [setTimeRange.toString()]: handleSetTimeRange,
  [signalExporting.toString()]: handleSingnalExporting,
  [checkSelectedWidgets.toString()]: handleCheckSelectedWidgets,
  [clearSelectedWidgets.toString()]: handleClearSelectedWidgets,
  // Dashboard filters tree
  [addSelectedFilter.toString()]: handleAddSelectedFilter,
  [removeSelectedFilter.toString()]: handleRemoveSelectedFilter,
  [clearSelectedFilters.toString()]: handleClearAllSelectedFilters,
  [addSelectedField.toString()]: handleAddSelectedField,
  [removeSelectedField.toString()]: handleRemoveSelectedField,
  [clearSelectedFields.toString()]: handleClearAllSelectedFields,
  [setTree.toString()]: handleSetTree,
  [requestTreeFiltersToogle.toString()]: handleChangeRequestTree,
  [clearTree.toString()]: handleClearTree,
  [upsertDashboardRequest.toString()]: handleUpsertDashboardRequest,
  [upsertDashboardSuccess.toString()]: handleUpsertDashboardSuccess,
};

const dashboardsReducer = createReducer(defaultState, handlers);

export default dashboardsReducer;
